import { createContext, Dispatch, ReactNode, SetStateAction, useContext, useEffect, useMemo, useState } from 'react'
import { ActionType } from 'src/components/Search/SearchDialog'
import { getRecentDistinctPaths, PosthogPath, updateRecentRoutes } from 'src/utils/posthog'
import { useAuth } from './AuthContext'

interface SearchContextType {
  actionWizard: ActionType
  setActionWizard: Dispatch<SetStateAction<ActionType>>
  recentRoutes: PosthogPath[]
  setRecentRoutes: Dispatch<SetStateAction<PosthogPath[]>>
  recentRoutesLoading: boolean
  setRecentRoutesLoading: Dispatch<SetStateAction<boolean>>
}

const SearchContext = createContext<SearchContextType>({} as SearchContextType)

export const useSearchContext = () => useContext(SearchContext)

export default function SearchProvider({ children }: { children: ReactNode }) {
  const { currentUser } = useAuth()
  const [recentRoutes, setRecentRoutes] = useState<PosthogPath[]>([])
  const [recentRoutesLoading, setRecentRoutesLoading] = useState<boolean>(true)
  const [actionWizard, setActionWizard] = useState<ActionType>(null)

  useEffect(() => {
    if (!currentUser?.email) return

    const getPosthogRoutes = async () => {
      setRecentRoutesLoading(true)
      const paths = await getRecentDistinctPaths(currentUser?.email)
      setRecentRoutes(paths)
      setRecentRoutesLoading(false)
    }

    getPosthogRoutes()
  }, [currentUser?.email])

  useEffect(() => {
    if (recentRoutesLoading) return

    const updatedRoutes = updateRecentRoutes(recentRoutes, document.title, location.pathname)
    setRecentRoutes(updatedRoutes)
  }, [recentRoutesLoading, location.pathname])

  const contextValue = useMemo(() => {
    return {
      actionWizard,
      setActionWizard,
      recentRoutes,
      setRecentRoutes,
      recentRoutesLoading,
      setRecentRoutesLoading
    }
  }, [actionWizard, setActionWizard, recentRoutes, setRecentRoutes, recentRoutesLoading, setRecentRoutesLoading])

  return <SearchContext.Provider value={contextValue}>{children}</SearchContext.Provider>
}
